
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";
import { Button } from '@/components/ui/button';
import { ReviewFormData } from '../review-form/useReviewForm';
import ReviewFormFields from './ReviewFormFields';
import useReviewForm from '../review-form/useReviewForm';
import useSubmitReview from './useSubmitReview';

interface SubmitReviewProps {
  initialCompany?: string;
}

const SubmitReviewForm = ({ initialCompany = '' }: SubmitReviewProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const reasonForExitMaxChars = 500;
  const biggestProblemMaxChars = 500;
  const { handleSubmitReview, isSubmitting } = useSubmitReview();

  const { 
    formData, 
    charCounts, 
    handleTextChange, 
    handleDateChange, 
    handleRadioChange, 
    handleCategoryChange, 
    handleTenureChange,
    handleLocationChange,
    handlePerkRatingsChange,
    handleSalaryChange, 
  } = useReviewForm(initialCompany);

  // Validate required fields
  const validateForm = (data: ReviewFormData) => {
    const requiredFields = {
      company: data.company?.trim(),
      jobTitle: data.jobTitle?.trim(),
      reasonForExit: data.reasonForExit?.trim(),
      location: data.location?.trim()
    };

    // Check if all required fields are filled and meet minimum requirements
    const isValid = 
      requiredFields.company && requiredFields.company.length >= 2 &&
      requiredFields.jobTitle && requiredFields.jobTitle.length >= 2 &&
      requiredFields.reasonForExit && requiredFields.reasonForExit.length >= 10 &&
      requiredFields.location;

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm(formData)) {
      toast({
        title: "Error",
        description: "Please fill out all required fields correctly. Company and job title need at least 2 characters, reason for exit needs at least 10 characters, and location is required.",
        variant: "destructive"
      });
      return;
    }

    try {
      await handleSubmitReview(formData);
      toast({
        title: "Success",
        description: "Review submitted successfully!",
      });
      navigate('/companies');
    } catch (error: any) {
      console.error("Error submitting review:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to submit review. Please try again.",
        variant: "destructive"
      });
    }
  };

  const isFormValid = validateForm(formData);

  return (
    <div className="bg-white shadow-sm rounded-lg border p-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-redflags-dark mb-2">
          Share Your Experience
        </h2>
        <p className="text-redflags-gray">
          Your honest review can help others make informed decisions.
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6 mt-6">
        <ReviewFormFields
          formData={formData}
          charCounts={charCounts}
          reasonForExitMaxChars={reasonForExitMaxChars}
          biggestProblemMaxChars={biggestProblemMaxChars}
          onTextChange={handleTextChange}
          onDateChange={handleDateChange}
          onRadioChange={handleRadioChange}
          onCategoryChange={handleCategoryChange}
          onTenureChange={handleTenureChange}
          onPerkRatingsChange={handlePerkRatingsChange}
          onLocationChange={handleLocationChange}
          onSalaryChange={handleSalaryChange}
          initialCompany={initialCompany}
        />
        
        <Button 
          type="submit" 
          className="w-full bg-redflags-red hover:bg-redflags-red/90 text-white" 
          disabled={isSubmitting || !isFormValid}
        >
          {isSubmitting ? "Submitting..." : "Submit Review"}
        </Button>
      </form>
    </div>
  );
};

export default SubmitReviewForm;
